/**
 * Read-only display of a learner's item rating (admin answer preview).
 *
 * @module ItemRatingReadOnly
 */
import { Check, Flag } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { RatingDetail } from '@/lib/ratingsApi';
import {
  reasonsFor,
  type RatingModule,
  type RatingTargetType,
} from '@/lib/ratingConstants';

export interface ItemRatingReadOnlyProps {
  readonly targetType: RatingTargetType;
  readonly module: RatingModule;
  readonly rating: RatingDetail | null;
}

function reasonLabel(
  code: string,
  targetType: RatingTargetType,
  module: RatingModule,
  t: (key: string, options?: { defaultValue?: string }) => string,
): string {
  const option = reasonsFor(targetType, module).find((item) => item.code === code);
  if (!option) return code;
  return t(option.labelKey, { defaultValue: code });
}

export function ItemRatingReadOnly({ targetType, module, rating }: ItemRatingReadOnlyProps) {
  const { t } = useTranslation();
  const isQuestion = targetType === 'question';
  const sectionLabel = isQuestion ? t('rating.questionLabel') : t('rating.feedbackLabel');
  const likeLabel = isQuestion ? t('rating.likeQuestionLabel') : t('rating.likeFeedbackLabel');
  const dislikeLabel = isQuestion ? t('rating.dislikeQuestionLabel') : t('rating.dislikeFeedbackLabel');

  if (!rating) {
    return (
      <div className="border-t border-border/50 pt-2">
        <span className="font-ui text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
          {sectionLabel}
        </span>
        <p className="font-ui mt-1 text-xs italic text-muted-foreground">
          {t('rating.admin.noFeedbackSubmitted', { defaultValue: 'No feedback submitted' })}
        </p>
      </div>
    );
  }

  const isLiked = rating.rating === 'like';
  const reasonCodes = rating.reasonCodes ?? [];
  const freeText = rating.freeText?.trim() ?? '';

  return (
    <div className="border-t border-border/50 pt-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-ui text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
          {sectionLabel}
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium',
            isLiked
              ? 'border-success/40 bg-success/10 text-success'
              : 'border-destructive/40 bg-destructive/10 text-destructive',
          )}
        >
          {isLiked ? (
            <Check className="h-3 w-3" aria-hidden />
          ) : isQuestion ? (
            <Flag className="h-3 w-3" aria-hidden />
          ) : null}
          <span className="font-ui">{isLiked ? likeLabel : dislikeLabel}</span>
        </span>
      </div>

      {!isLiked && reasonCodes.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {reasonCodes.map((code) => (
            <Badge key={code} variant="outline" className="font-ui text-[11px]">
              {reasonLabel(code, targetType, module, t)}
            </Badge>
          ))}
        </div>
      ) : null}

      {!isLiked && freeText ? (
        <p className="font-ui mt-2 whitespace-pre-wrap text-xs text-foreground/80">{freeText}</p>
      ) : null}
    </div>
  );
}
